import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Select, MenuItem } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { niiService } from '../../services/niiService';
import { NiiRunType, UploadResponse } from './types/nii.types';
import styles from './NiiLandingPage.module.css';

const NewAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();
  const queryClient = useQueryClient();

  // Form state
  const [auditName, setAuditName] = useState('');
  const [selectedGplVersion, setSelectedGplVersion] = useState('');
  const [dcgmFile, setDcgmFile] = useState<File | null>(null);
  const [dcgmUploadResult, setDcgmUploadResult] = useState<UploadResponse | null>(null);
  const [dcgmUploading, setDcgmUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch GPL scripts for dropdown
  const { data: gplScripts = [] } = useQuery({
    queryKey: ['gpl-script-bundles'],
    queryFn: () => niiService.listGplScripts(),
    refetchOnWindowFocus: false
  });

  // Upload node dump on file selection
  const handleDcgmUpload = async (file: File) => {
    setDcgmFile(file);
    setDcgmUploading(true);
    setDcgmUploadResult(null);
    try {
      const result = await niiService.uploadNodeDump(file);
      setDcgmUploadResult(result);
      addNotifications([
        {
          title: 'Upload Success',
          icon: 'icon-check',
          description: `Node dump uploaded: ${result.filename}`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (error) {
      addNotifications([
        {
          title: 'Upload Failed',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.detail || error.message : 'Failed to upload node dump.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      setDcgmFile(null);
    } finally {
      setDcgmUploading(false);
    }
  };

  // Submit — create GPL audit run
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await niiService.createRuns({
        runs: [
          {
            type: NiiRunType.GPL_AUDIT,
            name: auditName,
            gplVersion: selectedGplVersion,
            dataSource: {
              kind: 'upload',
              uploadId: dcgmUploadResult!.uploadId
            }
          }
        ]
      });
      // Invalidate execution table cache so it fetches fresh data immediately
      await queryClient.invalidateQueries({ queryKey: ['nii-runs'] });
      navigate('/nii/executions');
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError
            ? error.response?.data?.message || error.message
            : 'Failed to start audit.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    auditName.trim() !== '' &&
    selectedGplVersion !== '' &&
    dcgmUploadResult !== null &&
    !submitting;

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <div
        className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
      >
        <div className={styles.settingsTitleContainer}>
          {/* Header */}
          <div className={styles.settingsTitle}>New Audit</div>
          <p className={styles.subtitle}>
            Upload a node dump and select baseline Golden Parameter List (GPL) to perform Audit.
          </p>

          {/* Audit Name */}
          <div style={{ marginBottom: '20px', maxWidth: '300px' }}>
            <label style={{ fontWeight: 500 }}>
              Audit Name <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              className="input"
              placeholder="Type here"
              value={auditName}
              onChange={(e) => setAuditName(e.target.value)}
              style={{ width: '100%', marginTop: '4px' }}
            />
          </div>

          {/* Audit Type */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 500 }}>
              Audit Type <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
              {/* GPL Audit card — always selected */}
              <div
                style={{
                  borderLeft: '3px solid #0069c2',
                  border: '1px solid #0069c2',
                  padding: '16px 20px',
                  maxWidth: '300px',
                  cursor: 'default'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '8px' }}>GPL Audit</div>
                <p style={{ fontSize: '12px', color: '#878787', margin: 0 }}>
                  Runs GPL Provisioning scripts in MoShell Offline Mode. Diffs before/after state to find every
                  parameter deviation — 100% coverage with native conditional logic.
                </p>
              </div>
            </div>
          </div>

          {/* GPL Scripts dropdown */}
          <div style={{ marginBottom: '20px', maxWidth: '300px' }}>
            <label style={{ fontWeight: 500 }}>
              GPL Scripts <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <div style={{ marginTop: '4px' }}>
              <ReactEDSWrapper
                component={Select}
                type="input-single"
                label="Select GPL scripts"
                value={selectedGplVersion}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(raw: any) => {
                  const next = raw?.target?.value ?? raw;
                  const selected = Array.isArray(next) ? next[0] : next;
                  setSelectedGplVersion(typeof selected === 'string' ? selected : String(selected ?? ''));
                }}
              >
                {gplScripts.map((bundle) => (
                  <ReactEDSWrapper key={bundle.uploadId} component={MenuItem} value={bundle.gplVersion}>
                    {bundle.gplVersion}
                  </ReactEDSWrapper>
                ))}
              </ReactEDSWrapper>
            </div>
          </div>

          {/* DCGM Files drop zone */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 500 }}>
              DCGM Files{' '}
              <i className="icon icon-info" style={{ fontSize: '12px', opacity: 0.6 }} />
            </label>
            <div
              className="drag-and-drop"
              style={{ marginTop: '8px', maxWidth: '400px' }}
            >
              <form
                className="drag-and-drop-area"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files[0];
                  if (file && file.name.endsWith('.zip')) {
                    handleDcgmUpload(file);
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
                style={{ cursor: 'pointer', backgroundColor: dragOver ? 'rgba(0,105,194,0.05)' : undefined }}
              >
                <i className="icon icon-upload"></i>
                <p>
                  Drag & drop your file(s) here or<br />
                  <label role="button" style={{ color: '#0069c2', textDecoration: 'underline', cursor: 'pointer' }}>
                    select to browse
                  </label>
                </p>
                <p>
                  Supported format: .zip<br />
                  Max: 1 file
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleDcgmUpload(file);
                    e.target.value = '';
                  }}
                />
              </form>
            </div>

            {/* Uploaded file display */}
            {dcgmFile && (
              <div style={{ marginTop: '12px' }}>
                <span style={{ fontSize: '12px', color: '#878787' }}>Uploaded files:</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', padding: '6px 10px', border: '1px solid #878787', borderRadius: '4px', width: 'fit-content' }}>
                  <i className="icon icon-check" style={{ color: '#28a745' }} />
                  <span>{dcgmFile.name}</span>
                  <i
                    className="icon icon-cross"
                    style={{ cursor: 'pointer', opacity: 0.6 }}
                    onClick={(e) => { e.stopPropagation(); setDcgmFile(null); setDcgmUploadResult(null); }}
                  />
                </div>
              </div>
            )}
            {dcgmUploading && (
              <p style={{ marginTop: '8px', fontSize: '12px', color: '#878787' }}>Uploading and validating...</p>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <ReactEDSWrapper component={Button} onClick={() => navigate('/nii/audit-reports')}>
              Cancel
            </ReactEDSWrapper>
            <ReactEDSWrapper component={Button} primary onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? 'Starting…' : 'Start New Audit'}
            </ReactEDSWrapper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewAuditPage;
